import React from 'react';
import contensis from 'Common/api/contensis';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';
import { useRouter } from 'next/router';
import { Op, OrderBy } from 'contensis-delivery-api';

import Metadata from 'Components/Metadata';
import MainLayout from 'Layout/Main';
import Template, { datamap, PageData } from 'Templates/SolentStories/Listing';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

const GraduateStoriesListing: NextPageWithLayout<PageData> = ({
    entry,
    ancestors,
    stories,
    subjectsTaxonomy,
    levelOfStudyTaxonomy,
    featuredStories,
    losStories,
    activeFilterQueries
}) => {
    const router = useRouter();
    const metadata = cmsParser.metadata(entry);
    return (
        <>
            <Metadata
                {...metadata}
                canonical={
                    router.pathname +
                    (router.query.page && router.query.page !== '1'
                        ? `?page=${router.query.page}`
                        : '')
                }
                title={
                    metadata.title +
                    (router.query.page && router.query.page !== '1'
                        ? ` - page ${router.query.page}`
                        : '')
                }
            />
            <Template
                {...datamap({
                    entry,
                    stories,
                    ancestors,
                    losStories,
                    featuredStories,
                    subjectsTaxonomy,
                    levelOfStudyTaxonomy,
                    activeFilterQueries
                })}
            />
        </>
    );
};

GraduateStoriesListing.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ preview, res, query }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/graduate-stories`,
            entryLinkDepth: 1,
            entryFields: ['*']
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const subjectsTaxonomy = await client.taxonomy.getNodeByKey({
            key: '0/1259/1638',
            childDepth: 1
        });
        const levelOfStudyTaxonomy = await client.taxonomy.getNodeByKey({
            key: '0/1259/1541',
            childDepth: 1
        });

        // Function to parse filter query
        const parseFilterQuery = (query: string | string[] | undefined): string[] => {
            // If string, split commas
            if (typeof query === 'string') {
                return query.split(',').flat();
            }
            return [];
        };

        // Create subject filter query
        const parsedSubjectFilterQuery = parseFilterQuery(query.subject || []);
        const parsedLOSFilterQuery = parseFilterQuery(query.levelofstudy || []);

        const subjectValueQueries =
            parsedSubjectFilterQuery.length >= 1
                ? [...parsedSubjectFilterQuery.map(item => Op.contains('subject', item))]
                : [];

        const losValueQueries =
            parsedLOSFilterQuery.length >= 1
                ? [...parsedLOSFilterQuery.map(item => Op.contains('levelOfStudy', item))]
                : [];

        const allStories = [Op.equalTo('sys.contentTypeId', 'solentStory')];
        // Create full facilities query
        const storiesQuery = [
            Op.equalTo('sys.contentTypeId', 'solentStory'),
            Op.or(...subjectValueQueries),
            Op.or(...losValueQueries),
            Op.exists('sys.uri', true)
        ];
        const featuredStoryQuery = [
            Op.equalTo('sys.contentTypeId', 'solentStory'),
            Op.equalTo('featuredStory', true),
            Op.exists('sys.uri', true)
        ];

        const featuredStories = await search(featuredStoryQuery, {
            linkDepth: 1,
            orderBy: OrderBy.desc('sys.version.created'),
            fields: [
                'estimatedMinutesRead',
                'listingCardImage',
                'listingCardImageDefault',
                'listingCardImageOnHover',
                'courseCardQuote',
                'currentRole',
                'entryTitle',
                'cardImage',
                'student',
                'company',
                'version',
                'person',
                'sys'
            ]
        });

        const resStories = await search(storiesQuery, {
            pageSize: 12,
            pageIndex: parseInt(query.page as string) - 1 || 0,
            linkDepth: 3,
            orderBy: OrderBy.asc('entryTitle'),
            fields: [
                'listingCardImage',
                'listingCardImageDefault',
                'listingCardImageOnHover',
                'currentRole',
                'entryTitle',
                'cardImage',
                'student',
                'company',
                'person',
                'subject',
                ...excludeMetadata
            ]
        });

        const losStories = await search(allStories, {
            linkDepth: 2,
            pageSize: 300,
            fields: ['levelOfStudy']
        });

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        return {
            entry: resPage.entry,
            ancestors,
            levelOfStudyTaxonomy: levelOfStudyTaxonomy,
            subjectsTaxonomy: subjectsTaxonomy,
            featuredStories: featuredStories,
            losStories: losStories.items,
            stories: resStories,
            activeFilterQueries: {
                subject: parsedSubjectFilterQuery,
                levelOfStudy: parsedLOSFilterQuery
            }
        };
    });

    if (error) {
        return { notFound: true };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return { props: data };
    }
};

export default GraduateStoriesListing;
